import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../state'
import * as modals from '../../../modals'
import * as shared from '../../shared'
/**
 * Space functional usage component for presenting functional usage in
 * record details
 */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.notice.Component,
    ds.value.Component,
    modals.functionalUsage.Component,
    shared.functionalUsageFunctionalizations.Component,
  ],
  selector: 'atn-space-functional-usage',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** When true, the component is in read-only mode (e.g. record assigned to a cluster). */
  public readonly isReadonlySg = ngCore.input(false, { alias: 'readonly' })

  /** @see {@link state.space.full.records.plugins.functionalUsage.Plugin} */
  private readonly _functionalUsagePlugin = ngCore
    .inject(state.space.full.records.Collection)
    .plugins.lookup(state.space.full.records.plugins.functionalUsage.Plugin)
    .get()

  /** @see {@link state.space.full.functions.Collection} */
  private readonly _spaceFunctionsCollection = ngCore.inject(
    state.space.full.functions.Collection,
  )

  /** @see {@link state.space.full.serviceCenters.Collection} */
  private readonly _serviceCentersEc = ngCore.inject(
    state.space.full.serviceCenters.Collection,
  )

  /** @see {@link state.space.surveyConfiguration.Feature} */
  private readonly _surveyConfig = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )

  /** @see {@link state.space.full.warnings.plugin.Plugin} */
  public readonly recordWarningsPlugin = ngCore
    .inject(state.space.full.records.Collection)
    .plugins.lookup(state.space.full.warnings.plugin.Plugin)
    .get()

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** @see {@link ents.extra.space.Warning.Code} */
  public readonly WarningCode = ents.extra.space.Warning.Code

  /** Combined loading state from collection, feature, and record warnings */
  public readonly isFunctionalUsageLoadingSg = ngCore.computed(
    (
      isCollectionLoading = this._spaceFunctionsCollection.plugins.currentListPlugin.feature.selectors.isLoading.asSignal(),
      isFeatureLoading = this._functionalUsagePlugin.selectors.isLoading.asSignal(),
      isRecordWarningsLoading = this.recordWarningsPlugin.selectors.isLoading.asSignal(),
    ) => isCollectionLoading || isFeatureLoading || isRecordWarningsLoading,
  )

  /** Generated chart data @see {@link ents.core.display.chart.data.Slice[]}  */
  public readonly functionalUsageChartDataSg = ngCore.computed(
    (
      functionalUsage = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
      spaceFunctions = this._spaceFunctionsCollection.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => {
      if (!functionalUsage?.functionalizations) {
        return { slices: [] }
      }

      // Create a map of function IDs to codes
      const functionCodeMap = new Map(
        spaceFunctions
          .filter(TypeChecker.isNotNullish.raw)
          .map(func => [func.id, func.code]),
      )

      return {
        slices: [...functionalUsage.functionalizations]
          .sort((a, b) => b.percent - a.percent)
          .map(func => ({
            name: functionCodeMap.get(func.functionId) ?? func.functionId,
            value: func.percent * 100,
          })),
      }
    },
  )

  /**  @see {@link ents.extra.space.Record.FunctionalUsage.Methodology methodology} */
  public readonly methodologySg = ngCore.computed(
    (
      functionalUsage = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
    ) => functionalUsage?.functionalizationMethodology,
  )

  /**
   * Methodology display label.
   * When methodology is {@link ents.extra.space.Record.FunctionalUsage.Methodology.ServiceCenter},
   * appends the service center name: e.g. `"Service Center: North Wing"`
   */
  public readonly methodologyLabelSg = ngCore.computed(
    (
      methodology = this.methodologySg(),
      serviceCenter = this.serviceCenterSg(),
    ) =>
      serviceCenter ? `${methodology}: ${serviceCenter.name}` : methodology,
  )

  /** Current service center record for the functional usage (if methodology is {@link ents.extra.space.Record.FunctionalUsage.Methodology.ServiceCenter}) */
  public readonly serviceCenterSg = ngCore.computed(
    (
      functionalUsage = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
    ) =>
      functionalUsage?.serviceCenterId
        ? this._serviceCentersEc.plugins.cachePlugin.feature.selectors
            .getCachedEntity(functionalUsage.serviceCenterId)
            .asSignal()
        : undefined,
  )

  /** Whether the current methodology supports function allocations */
  public readonly isAssignableMethodologySg = ngCore.computed(
    (methodology = this.methodologySg()) =>
      TypeChecker.isNotNullish(methodology) &&
      ents.extra.space.Record.FunctionalUsage.Methodology.isAssignable(
        methodology,
      ),
  )

  /** All allocations including ones not in current usage (with 0%) */
  public readonly allAllocationsSg = ngCore.computed<
    shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent[]
  >(
    (
      functionalUsage = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
      spaceFunctions = this._spaceFunctionsCollection.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      surveyConfig = this._surveyConfig.selectors.config.asSignal(),
    ) => {
      const existingMap = new Map(
        (functionalUsage?.functionalizations ?? []).map(f => [
          f.functionId,
          f.percent,
        ]),
      )

      const isManualMethodology =
        functionalUsage?.functionalizationMethodology ===
        ents.extra.space.Record.FunctionalUsage.Methodology.Manual

      const getTagsForFunction = (
        func: ents.extra.space.Function,
      ): shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent['tags'] => {
        const tags: shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent['tags'] =
          []

        if (!isManualMethodology) {
          return tags
        }

        if (
          surveyConfig?.functionalUsage
            ?.requireSponsoredAccountAssignmentsForSponsoredFunctionalUsages &&
          func.isSponsored
        ) {
          tags.push({
            text: 'Sponsored',
            style: ds.color.Legacy.PredefinedStyle.Info,
          })
        }

        if (
          surveyConfig?.functionalUsage
            ?.requireFunctionalUsageAssignmentsForSelectedAccounts &&
          func.isRequiredForMatchingPayroll
        ) {
          tags.push({
            text: 'Required',
            style: ds.color.Legacy.PredefinedStyle.Warning,
          })
        }

        return tags
      }

      // Display all non-exclusive space functions with their values or 0%
      return spaceFunctions
        .filter(TypeChecker.isNotNullish.raw)
        .filter(func => !func.isExclusive)
        .map(func => ({
          ...func,
          percent: existingMap.get(func.id) ?? 0,
          tags: getTagsForFunction(func),
        }))
    },
  )

  /** Edit record data */
  public editClickHandler(): void {
    this._functionalUsagePlugin.actions.beginUpdating
      .from('Space.RecordDetails.FunctionalUsage.Edit.Clicked')
      .dispatch()
  }
}
